import { useKeystoneSession } from '../../auth/use-keystone-session';
import { Box, Button, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react';
import { type KeyboardEvent, useEffect, useRef, useState, type JSX } from 'react';

import { createCustomer, crmLoadCustomer, type HubSpotContactMatch, searchHubSpotContacts } from '@external';
import { isValidEmail, isValidZipcode } from '@helpers';
import { lang } from '@lang';
import { selectGetNewCustomerData, setGetNewCustomerData, useAppDispatch, useAppSelector } from '@state';

type ModalStep = 'search' | 'create';

export const GetNewCustomerDataModal = (): JSX.Element => {
	const { getToken } = useKeystoneSession();

	const dispatch = useAppDispatch();
	const state = useAppSelector(selectGetNewCustomerData);

	const [modalStep, setModalStep] = useState<ModalStep>('search');
	const [email, setEmail] = useState<string>('');
	const [firstName, setFirstName] = useState<string>('');
	const [lastName, setLastName] = useState<string>('');
	const [phone, setPhone] = useState<string>('');
	const [zipcode, setZipcode] = useState<string>('');
	const [customer, setCustomer] = useState<any | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [contactMatches, setContactMatches] = useState<Array<HubSpotContactMatch>>([]);
	const matchSearchSeq = useRef(0);

	// When customer is found/created, close the modal and pass data through
	useEffect(() => {
		if (customer) dispatch(setGetNewCustomerData({ isOpen: false, data: customer }));
	}, [customer]);

	// Debounced HubSpot contact lookup — surfaces near-miss contacts (typo'd domains) while typing
	useEffect(() => {
		const seq = ++matchSearchSeq.current;

		if (modalStep !== 'search' || email.length < 3) {
			setContactMatches([]);
			return undefined;
		}

		const timer = setTimeout(async () => {
			const [matches] = await searchHubSpotContacts({ search: email, getToken });
			if (seq === matchSearchSeq.current) setContactMatches(matches ?? []);
		}, 500);

		return () => { clearTimeout(timer); };
	}, [email, modalStep]);

	const resetForm = (): void => {
		setEmail('');
		setFirstName('');
		setLastName('');
		setPhone('');
		setZipcode('');
		setError(null);
		setCustomer(null);
		setContactMatches([]);
		setIsLoading(false);
		setModalStep('search');
	};

	const onClose = (): void => {
		dispatch(setGetNewCustomerData({ isOpen: false, data: customer }));
		resetForm();
	};

	const handleSearch = async (searchEmail: string = email): Promise<void> => {
		if (!isValidEmail(searchEmail)) {
			setError(lang.customer.message.invalidEmail);
			return;
		}

		setIsLoading(true);
		setError(null);
		setContactMatches([]);

		const [result, err] = await crmLoadCustomer({ email: searchEmail, getToken });

		setIsLoading(false);

		if (err || !result) {
			// Customer not found -- transition to create form instead of showing a toast
			setModalStep('create');
			return;
		}

		// Customer found -- set state to trigger modal close and navigation
		setCustomer(result);
	};

	const handleCreate = async (): Promise<void> => {
		setIsLoading(true);
		setError(null);

		const [result, err] = await createCustomer({
			email,
			firstName,
			lastName,
			phone: phone || undefined,
			zipcode: zipcode || undefined,
			getToken
		});

		setIsLoading(false);

		if (err || !result) {
			setError(lang.customer.message.createCustomerFailed);
			return;
		}

		// Customer created -- set state to trigger modal close and navigation
		setCustomer(result);
	};

	const handleBack = (): void => {
		setModalStep('search');
		setFirstName('');
		setLastName('');
		setPhone('');
		setZipcode('');
		setError(null);
	};

	const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
		if (e.key === 'Enter') handleSearch();
	};

	const handleSelectMatch = (match: HubSpotContactMatch): void => {
		setEmail(match.email);
		setContactMatches([]);
		handleSearch(match.email);
	};

	const isSearchDisabled = !isValidEmail(email);
	const isCreateDisabled = !firstName.trim() || !lastName.trim() || !isValidZipcode(zipcode);

	return (
		<Modal isOpen={state.isOpen} onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalCloseButton />
				<ModalHeader>
					{modalStep === 'search' ? lang.customer.label.newCustomer : lang.customer.label.createCustomer}
				</ModalHeader>
				<ModalBody>
					{modalStep === 'search' && (
						<Box>
							<Text fontSize={'md'}>{lang.customer.label.customerEmail}</Text>
							<Input
								type="email"
								size="md"
								value={email}
								onChange={(e) => { setEmail(e.target.value.trim().toLowerCase()); }}
								onKeyDown={handleSearchKeyDown}
							/>
							{email.length > 0 && isSearchDisabled && (
								<Text fontSize="xs" color="red.500" mt={1}>{lang.customer.message.invalidEmail}</Text>
							)}
							{error && (
								<Text fontSize="sm" color="red.500" mt={1}>{error}</Text>
							)}
							{contactMatches.length > 0 && (
								<Box mt={2}>
									<Text fontSize="xs" color="gray.500">{lang.customer.text.hubspotMatches}</Text>
									<Box mt={1} borderWidth="1px" borderRadius="md" maxH="200px" overflowY="auto">
										{contactMatches.map((match) => (
											<Box
												key={match.contactId}
												px={3}
												py={2}
												cursor="pointer"
												_hover={{ bg: 'gray.100' }}
												onClick={() => { handleSelectMatch(match); }}
											>
												<Text fontSize="sm">{match.firstName} {match.lastName}</Text>
												<Text fontSize="xs" color="gray.500">{match.email}</Text>
											</Box>
										))}
									</Box>
								</Box>
							)}
						</Box>
					)}
					{modalStep === 'create' && (
						<Box display="flex" flexDirection="column" gap={4}>
							<Text fontSize="sm" color="gray.500">
								{lang.customer.text.noCustomerFound}
							</Text>
							<FormControl>
								<FormLabel fontSize="sm">{lang.customer.label.customerEmail}</FormLabel>
								<Input type="email" size="md" value={email} isReadOnly bg="gray.50" />
							</FormControl>
							<FormControl isRequired>
								<FormLabel fontSize="sm">{lang.customer.label.firstName}</FormLabel>
								<Input
									type="text"
									size="md"
									value={firstName}
									onChange={(e) => { setFirstName(e.target.value); }}
									autoFocus
								/>
							</FormControl>
							<FormControl isRequired>
								<FormLabel fontSize="sm">{lang.customer.label.lastName}</FormLabel>
								<Input
									type="text"
									size="md"
									value={lastName}
									onChange={(e) => { setLastName(e.target.value); }}
								/>
							</FormControl>
							<FormControl>
								<FormLabel fontSize="sm">{lang.customer.label.phone}</FormLabel>
								<Input
									type="tel"
									size="md"
									value={phone}
									onChange={(e) => { setPhone(e.target.value); }}
								/>
							</FormControl>
							<FormControl isRequired>
								<FormLabel fontSize="sm">{lang.customer.label.zipcode}</FormLabel>
								<Input
									type="text"
									size="md"
									value={zipcode}
									onChange={(e) => { setZipcode(e.target.value); }}
								/>
							</FormControl>
							{error && (
								<Text fontSize="sm" color="red.500">{error}</Text>
							)}
						</Box>
					)}
				</ModalBody>
				<ModalFooter>
					{modalStep === 'search' && (
						<>
							<Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
							<Button
								colorScheme="green"
								onClick={() => { handleSearch(); }}
								isLoading={isLoading}
								isDisabled={isSearchDisabled}
							>
								Search
							</Button>
						</>
					)}
					{modalStep === 'create' && (
						<>
							<Button variant="ghost" mr={3} onClick={handleBack}>Back</Button>
							<Button
								colorScheme="green"
								onClick={handleCreate}
								isLoading={isLoading}
								isDisabled={isCreateDisabled}
							>
								{lang.customer.label.createCustomer}
							</Button>
						</>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
